// Dev-only fixtures for the virtualized chat — seeded message sets the
// testbar buttons drop into the message store so the scroll strategy can be
// exercised under realistic load. Lives outside the component so the
// production rendering path doesn't carry ~250 lines of test data; the
// component delegates the testbar's `Add test msgs` and `+1k/150img`
// buttons straight into here.
//
// Pure data + store writes — no DOM, no signals, no strategy hooks. The
// component still owns `purgeAllMessages()` because that needs to reset
// its local `_allMessages` signal and the strategy's height cache, neither
// of which makes sense to plumb through a service.
import { Injectable } from '@angular/core';
import {
  Message,
  MessageDraft,
  MessageStore,
  TextMessagePayload,
} from '../../../state/messages.repository';
import { UserSettingsStore } from '../../../state/user-settings.repository';
import { EmbedResolverService } from '../../../core/services/embed-resolver.service';

@Injectable({ providedIn: 'root' })
export class ChatFixtureService {
  constructor(
    private _messageStore: MessageStore,
    private _userSettings: UserSettingsStore,
    private _embedResolver: EmbedResolverService
  ) {}

  // Triggers OGP resolution for seeded URL-bearing messages. Fixture
  // messages bypass `ChatService.buildMessage` (they go straight to
  // `messageStore.add`), which is where the real-send pipeline triggers
  // OGP — without this helper, fixture URLs would render as bare links
  // with no preview card. Resolver handles the cache / dedup / timeout
  // / prune-race guard; this is just the dispatch loop.
  private _resolveSeededEmbeds(messages: Message[]): void {
    for (const msg of messages) {
      void this._embedResolver.resolveForMessage(msg);
    }
  }

  // The attachment image the fixtures share, fetched once and inlined as a
  // data URL — the form real senders use and the only one `BlobUrlPipe`
  // renders.
  private _dummyImage?: Promise<string>;

  private _loadDummyImage(): Promise<string> {
    this._dummyImage ??= fetch(DUMMY_IMAGE_PATH)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      );
    return this._dummyImage;
  }

  // 8 messages covering every (origin × scope × breakout) combination the
  // chat needs to render distinctly, plus one image attachment. Useful for
  // eyeballing layout deltas after a chat-msg style change.
  async addTestMessages(): Promise<void> {
    const dummyImage = await this._loadDummyImage();
    const now = Date.now();
    const person = (name: string, id: string) => ({
      name,
      id,
      avatar: '',
    });
    const roomId = this._userSettings.value.roomId || 'test-room';

    const drafts: MessageDraft[] = [
      {
        time: new Date(now + 1),
        origin: 'self',
        scope: 'room',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('You', 'own'),
        payload: { text: 'origin=self · scope=room · breakoutRoomId="" · main-room broadcast' },
      },
      {
        time: new Date(now + 2),
        origin: 'remote',
        scope: 'room',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('Bob', 'bob'),
        payload: { text: 'origin=remote · scope=room · breakoutRoomId="" · main-room broadcast' },
      },
      {
        time: new Date(now + 3),
        origin: 'self',
        scope: 'room',
        roomId,
        breakoutRoomId: 'breakout-A',
        kind: 'message',
        sender: person('You', 'own'),
        payload: {
          text: 'origin=self · scope=room · breakoutRoomId="breakout-A" · own msg in breakout',
        },
      },
      {
        time: new Date(now + 4),
        origin: 'remote',
        scope: 'room',
        roomId,
        breakoutRoomId: 'breakout-B',
        kind: 'message',
        sender: person('Charlie', 'charlie'),
        payload: {
          text: 'origin=remote · scope=room · breakoutRoomId="breakout-B" · remote in breakout',
        },
      },
      {
        time: new Date(now + 5),
        origin: 'self',
        scope: 'private',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('You', 'own'),
        receiver: person('Alice', 'alice'),
        payload: { text: 'origin=self · scope=private · to=Alice · DM I sent' },
      },
      {
        time: new Date(now + 6),
        origin: 'remote',
        scope: 'private',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('Diana', 'diana'),
        receiver: person('You', 'own'),
        payload: { text: 'origin=remote · scope=private · from=Diana · DM I received' },
      },
      {
        time: new Date(now + 7),
        origin: 'system',
        scope: 'system',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('System', 'system'),
        payload: { text: 'origin=system · scope=system · breakoutRoomId ignored · system notice' },
      },
      // Image attachment, inlined like a real send (see `_loadDummyImage`).
      {
        time: new Date(now + 8),
        origin: 'remote',
        scope: 'room',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('Eve', 'eve'),
        payload: {
          text: `origin=remote · scope=room · attachment=${DUMMY_IMAGE_PATH}`,
          attachment: {
            type: 'image',
            data: dummyImage,
            ...DUMMY_IMAGE_DIMS,
          },
        },
      },
      // URL-bearing message — `EmbedResolverService` produces the preview
      // card; the fixture only needs to ship a message whose text contains
      // a URL. (Real users can't craft `payload.embedUrl` manually — it's
      // only populated by the resolver after receive.) The dispatch happens
      // in `_resolveSeededEmbeds` below, after `messageStore.add` returns.
      {
        time: new Date(now + 9),
        origin: 'remote',
        scope: 'room',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender: person('Frank', 'frank'),
        payload: {
          text: `Check this out: ${FIXTURE_EMBED_HREF}`,
        },
      },
    ];

    const stored = drafts.map((d) => this._messageStore.add(d));
    // Fire-and-forget OGP resolution for any URL-bearing seeded messages.
    void this._resolveSeededEmbeds(stored);
  }

  // Stress fixture for the 1k / 150-image scenario from the branch
  // description. Generates N text messages with M random images interleaved
  // so the prototype can be profiled at the high end. Synchronous adds —
  // expect a brief jank spike on press, that's intentional.
  //
  // Row heights are deliberately varied: a uniform-ish text body made every
  // row land near the strategy's 80 px default, which hid the scroll-jump
  // bugs that show up as soon as real rows differ in height. The pool below
  // covers single-token reactions, one-liners, mid paragraphs, very long
  // paragraphs, and fenced code blocks (rendered through the same markdown
  // pipeline ChatService uses, so the DOM matches a real `ts` block — `<pre>`
  // + highlight.js spans + the `.copy` button hook).
  async addStressFixture(count = 1000, imageCount = 150): Promise<void> {
    // Real asset rather than a 1x1 PNG — the whole point of the stress
    // fixture is to surface decoded-image memory and lazy-load layout cost,
    // and a 1x1 placeholder bypasses both. Same file as the "Add test msgs"
    // image draft for consistency.
    const dummyImage = await this._loadDummyImage();
    const now = Date.now();
    const roomId = this._userSettings.value.roomId || 'test-room';
    const person = (name: string, id: string) => ({
      name,
      id,
      avatar: '',
    });
    const senders = [
      person('Alice', 'alice'),
      person('Bob', 'bob'),
      person('Charlie', 'charlie'),
      person('Diana', 'diana'),
    ];
    const imageEvery = Math.max(1, Math.floor(count / imageCount));
    // Slot 13 is coprime with imageEvery=6, so the shape rotation doesn't
    // align with the image cadence and every shape gets hit with and without
    // an image attached. Returns raw markdown source — `ChatMsg`
    // renders source → HTML at display time, mirroring what receive-path
    // messages look like in storage.
    const pickText = (i: number): string => {
      const slot = i % 13;
      if (slot === 0) return `#${i} ${STRESS_SHORT[i % STRESS_SHORT.length]}`;
      if (slot < 5) return `#${i} ${STRESS_ONE_LINERS[i % STRESS_ONE_LINERS.length]}`;
      if (slot < 9) return `#${i} ${STRESS_PARAGRAPHS[i % STRESS_PARAGRAPHS.length]}`;
      if (slot < 12) return `#${i} ${STRESS_LONG[i % STRESS_LONG.length]}`;
      return `#${i} code sample:\n\n${STRESS_CODEBLOCKS[i % STRESS_CODEBLOCKS.length]}`;
    };
    // URL injection cadence — every 17th message swaps its text for a
    // URL-bearing variant. ~50 URL messages across the run, dense enough
    // that one lands near the bottom on first paint. Coprime with
    // `imageEvery` (6) so image and URL cadences don't fully align.
    // Note: fixture messages bypass `ChatService.buildMessage`, so the OGP
    // resolver doesn't run — these render as clickable links via the
    // markdown helper but won't sprout embed cards. Use a real message
    // through the chat input to see the embed preview.
    const urlEvery = 17;
    const seededUrlMessages: Message[] = [];
    for (let i = 0; i < count; i++) {
      const sender = senders[i % senders.length];
      const isImage = i % imageEvery === 0 && i < imageCount * imageEvery;
      const isUrl = i > 0 && i % urlEvery === 0 && !isImage;
      const text = isUrl ? `#${i} sharing a link — ${FIXTURE_EMBED_HREF}` : pickText(i);
      const payload: TextMessagePayload = isImage
        ? { text, attachment: { type: 'image', data: dummyImage, ...DUMMY_IMAGE_DIMS } }
        : { text };
      const draft: MessageDraft = {
        time: new Date(now - (count - i) * 1000),
        origin: i % 7 === 0 ? 'self' : 'remote',
        scope: 'room',
        roomId,
        breakoutRoomId: '',
        kind: 'message',
        sender,
        payload,
      };
      const added = this._messageStore.add(draft);
      if (isUrl) seededUrlMessages.push(added);
    }
    // Fire-and-forget OGP for the ~50 URL-bearing messages. Cached per URL
    // so the single fixture link only hits the backend once.
    void this._resolveSeededEmbeds(seededUrlMessages);
  }
}

// Absolute because a room URL like `/<roomId>` would otherwise resolve a
// relative `assets/...` to `/<roomId>/assets/...` and 404.
const DUMMY_IMAGE_PATH = '/assets/bg_waves_fancy.webp';

// Intrinsic dimensions of `DUMMY_IMAGE_PATH`. Hardcoded so the
// dev fixtures exercise the same `<img width height>` reservation path that
// real user-sent images go through — without these, fixture-generated rows
// would fall back to the legacy "decode then reflow" path and mask any
// regression in the dim-capture code at send time.
const DUMMY_IMAGE_DIMS = { width: 1920, height: 1280 } as const;

// Pre-baked embed cards rotated through the stress fixture. Same three
// render branches as the basic test set (full / text-only / minimal), plus
// a long-title variant for wrap testing and a long-description variant for
// vertical-growth testing. Images point to local assets so the fixture
// stays network-independent.
// Real URL used as link content in the URL-bearing fixture messages so
// clicking the rendered `<a>` opens an actual page. OGP-resolved embed
// cards only appear for messages that go through `ChatService.buildMessage`
// (live sends/receives) — fixtures bypass that path.
const FIXTURE_EMBED_HREF = 'https://maxroll.gg/poe2/planner/';

const STRESS_SHORT = ['👍', 'ok', 'lol', 'noted', '🔥🔥🔥', '+1', 'lgtm'];

const STRESS_ONE_LINERS = [
  'just a quick note about the prototype',
  'thinking about it now — give me a sec',
  'we should probably revisit this after lunch',
  'looks fine to me, ship it',
  'see you in standup',
  'pushed a fix to the branch',
  'tried it on Safari, same issue',
];

const STRESS_PARAGRAPHS = [
  'Medium-length message: we need enough text here to occupy a couple of lines so the virtual scroll has something interesting to measure when the row actually paints. Otherwise every row lands near the strategy default and the bug stays invisible.',
  'Another mid-length one. The point is to give the height cache and prefix-sum a real distribution rather than a uniform estimate across every single row — that is closer to how real conversations look and surfaces the re-anchor bugs much faster.',
  'When this row renders, the ResizeObserver should pick up the actual border-box height; in the meantime the 80px default kicks in and the offsets shuffle around it. Watching the scrollbar during cold load is the easiest way to see whether the shuffle is anchored or jumps.',
];

const STRESS_LONG = [
  'Long: ' +
    'lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. '.repeat(
      4
    ),
  'Very long: ' +
    'duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. '.repeat(
      10
    ),
];

const STRESS_CODEBLOCKS = [
  // small
  ['```ts', 'const double = (x: number) => x * 2;', 'console.log(double(21));', '```'].join('\n'),
  // wide single line — exercises the bubble's per-block horizontal
  // scrollbar (`pre.block .scroller { overflow: auto }`). Without
  // `min-width: 0` along the chain the bubble would force the row past
  // the viewport instead and the chat would gain a viewport-wide
  // horizontal scrollbar.
  [
    '```ts',
    'const longLine = await db.users.findManyWithProfilesAndPermissionsScopedByTenantAndIncludingSoftDeletedRows({ tenantId: ctx.tenantId, includeArchived: true, withPermissions: true });',
    '```',
  ].join('\n'),
  // medium
  [
    '```ts',
    'function fibonacci(n: number): number {',
    '  if (n <= 1) return n;',
    '  let prev = 0;',
    '  let curr = 1;',
    '  for (let i = 2; i <= n; i++) {',
    '    const next = prev + curr;',
    '    prev = curr;',
    '    curr = next;',
    '  }',
    '  return curr;',
    '}',
    'console.log(fibonacci(20));',
    '```',
  ].join('\n'),
  // large — exercises tall rows so the height delta on first measure is
  // obvious. Pulled from the scroll-strategy source so it parses as real ts.
  [
    'Here is a chunk of the scroll strategy for height-cache testing:',
    '',
    '```ts',
    'export class MessageListScrollStrategy implements VirtualScrollStrategy {',
    '  private _viewport: CdkVirtualScrollViewport | null = null;',
    '  private _heights = new Map<string, number>();',
    '  private _offsets: number[] | null = null;',
    '',
    '  attach(viewport: CdkVirtualScrollViewport): void {',
    '    this._viewport = viewport;',
    '    this._invalidateOffsets();',
    '    this._update();',
    '  }',
    '',
    '  setHeight(id: string, height: number): boolean {',
    '    const rounded = Math.round(height);',
    '    const prev = this._heights.get(id);',
    '    if (prev === rounded) return false;',
    '    this._heights.set(id, rounded);',
    '    this._invalidateOffsets();',
    '    this._update();',
    '    return true;',
    '  }',
    '',
    '  private _ensureOffsets(): void {',
    '    if (this._offsets) return;',
    '    if (!this._viewport) {',
    '      this._offsets = [0];',
    '      return;',
    '    }',
    '    const len = this._viewport.getDataLength();',
    '    const arr = new Array<number>(len + 1);',
    '    arr[0] = 0;',
    '    for (let i = 0; i < len; i++) {',
    '      arr[i + 1] = arr[i] + this._heightFor(i);',
    '    }',
    '    this._offsets = arr;',
    '  }',
    '',
    '  private _indexForOffset(offset: number): number {',
    '    this._ensureOffsets();',
    '    const arr = this._offsets!;',
    '    if (arr.length <= 1) return 0;',
    '    let lo = 0;',
    '    let hi = arr.length - 1;',
    '    while (lo < hi) {',
    '      const mid = (lo + hi + 1) >>> 1;',
    '      if (arr[mid] <= offset) lo = mid;',
    '      else hi = mid - 1;',
    '    }',
    '    return lo;',
    '  }',
    '}',
    '```',
  ].join('\n'),
];
